import cn from '../../../common/className.js'
import Select from '../../../common/components/Select.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import {useSelector} from '../../../store.js'
import {
  isLabelPropertyEnabledSelector,
  labelConfigSelector,
} from '../../../data/labelInfos/index.js'
import useLabelProperty, {formatMetaClassName} from './useLabelProperty.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'
import ShipperOptions from '../../../data/shipperOptions.js'
import OverrideLabelProperty from './OverrideLabelProperty.js'

/**
 * Common select component for label info config
 *
 * @param {Object} props
 * @param {String} [props.label]
 * @param {Function} [props.Label]
 * @param {String} [props.id]
 * @param {String} [props.className]
 * @param {Boolean} [props.hide]
 * @param {Boolean} [props.disabled]
 * @param {String} [props.errorMessage]
 * @param {Boolean} [props.required]
 * @param {Boolean} [props.noEmptyOption]
 * @param {Any} [props.value]
 * @param {Array} [props.options]
 * @param {Function} props.onChange
 * @param {Node} [props.children]
 * @param {(String|Boolean)} [props.labelProperty]
 * @param {Object} [props.propertySpecs]
 * @param {Function} props.propertySpecs.labelPropertiesFunc
 * @param {Array} props.propertySpecs.validShipperTypes
 * @param {Function} props.propertySpecs.dependentPropertiesFunc
 * @param {Function} props.propertySpecs.validLabelPropertiesFunc
 * @param {Function} [props.labelPropertyCheckedSelector]
 * @param {String} [props.shipperOptionsProperty]
 * @param {Boolean} [props.preventPropertyDisable]
 * @param {String} [overrideLabel]
 * @param {String} [overrideHelpLink]
 * @param {String} [overrideHelpTitle]
 * @returns {React.ReactNode}
 */
export default function ConfigSelect({
  className,
  id,
  label,
  Label,
  value,
  options,
  hide,
  disabled,
  errorMessage,
  required,
  noEmptyOption,
  onChange,
  children,
  propertySpecs,
  labelProperty,
  labelPropertyCheckedSelector,
  shipperOptionsProperty,
  preventPropertyDisable,
  overrideLabel,
  overrideHelpLink,
  overrideHelpTitle,
}) {
  let {labelInfoID, shipperType, isLabelEnableMode, isSummary, isPickMode} =
    useLabelConfigContext()

  labelProperty = labelProperty || ''
  id = id || `${labelInfoID}__${labelProperty}`

  useLabelProperty({
    labelProperty,
    propertySpecs,
  })

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty,
    }),
  )

  value = useSelector((state) =>
    value === undefined
      ? labelConfigSelector(state, {labelInfoID})[labelProperty]
      : value,
  )

  options =
    options ||
    (shipperOptionsProperty &&
      ShipperOptions[shipperType][shipperOptionsProperty]) ||
    undefined

  // if the value is undefined for some reason then show empty option
  // only for selects with a force "noEmptyOption"
  noEmptyOption =
    noEmptyOption === true && value === undefined ? false : noEmptyOption

  // enforce a value
  value ||= ''

  hide ||=
    !!shipperType && !propertySpecs.validShipperTypes.includes(shipperType)

  disabled ||= isLabelEnableMode && !isLabelPropertyEnabled

  if (hide) {
    return null
  }

  if (isSummary) {
    return label
  }

  if (isPickMode && !isLabelPropertyEnabled) {
    return (
      <PickLabelProperty
        label={label}
        labelProperty={labelProperty}
        checkedSelector={labelPropertyCheckedSelector}
      />
    )
  }

  return (
    <li
      className={cn`list__item--shipping-options list__item--shipping-options-select ${formatMetaClassName(
        labelProperty,
      )} ${{'property-disabled': disabled}} ${className}`}
    >
      <div
        className={cn`wrap--edit-preset-form-input ${
          preventPropertyDisable && 'wrap--edit-preset-prevent-disable'
        }`}
      >
        {Label ? (
          <span className="label--config__txt">
            <Label />
          </span>
        ) : null}
        <Select
          className="w-100"
          labelClassName="label--bold label--config__txt"
          label={label}
          id={id}
          onChange={(newValue) =>
            onChange(newValue === 'None' ? null : newValue)
          }
          value={value}
          disabled={disabled}
          options={options}
          errorMessage={errorMessage}
          required={required}
        >
          {!noEmptyOption && <option value=""></option>}
          {children}
        </Select>
      </div>
      <EnableLabelProperty
        labelProperty={labelProperty}
        checkedSelector={labelPropertyCheckedSelector}
      />
      {overrideLabel && (
        <OverrideLabelProperty
          propertySpecs={propertySpecs}
          label={overrideLabel}
          labelPropertyCheckedSelector={labelPropertyCheckedSelector}
          helpLink={overrideHelpLink}
          helpTitle={overrideHelpTitle}
        />
      )}
    </li>
  )
}
